import "@/Portfolio.css";
import "@/HeroEnhancements.css";
import { HeroOrbit, ScrollCue } from "@/components/HeroOrbit";
import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { AnimatePresence, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUp, ArrowUpRight, Check, Instagram, Mail, Menu, MoveRight, X } from "lucide-react";

const heroImage = "https://images.unsplash.com/photo-1706353399656-210cca727a33?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxwaHlzaW90aGVyYXBpc3QlMjBzcG9ydHMlMjBmaXRuZXNzJTIwdHJhaW5lciUyMGNvYWNoaW5nfGVufDB8fHx8MTc4NzgxMDE4N3ww&ixlib=rb-4.1.0&q=85";
const aboutImage = "https://images.unsplash.com/photo-1649751361457-01d3a696c7e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxwaHlzaW90aGVyYXBpc3QlMjBzcG9ydHMlMjBmaXRuZXNzJTIwdHJhaW5lciUyMGNvYWNoaW5nfGVufDB8fHx8MTc4NzgxMDE4N3ww&ixlib=rb-4.1.0&q=85";
const trainingImage = "https://images.unsplash.com/photo-1648542036561-e1d66a5ae2b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxwaHlzaW90aGVyYXBpc3QlMjBzcG9ydHMlMjBmaXRuZXNzJTIwdHJhaW5lciUyMGNvYWNoaW5nfGVufDB8fHx8MTc4NzgxMDE4N3ww&ixlib=rb-4.1.0&q=85";

const navItems = [
  ["About", "about"],
  ["Expertise", "expertise"],
  ["Experience", "experience"],
  ["Work", "work"],
];

const marqueeItems = ["Neck pain", "Back pain", "Posture", "Mobility", "Strength", "Online care", "Confidence"];

const skills = [
  {
    number: "01",
    title: "Physiotherapy Assessment",
    text: "Understand what is driving discomfort through an individual, movement-led clinical assessment.",
    items: ["Musculoskeletal assessment", "Posture and movement analysis", "Neck and back pain assessment", "Functional mobility evaluation", "Pain trigger identification", "Personalized treatment planning"],
  },
  {
    number: "02",
    title: "Rehabilitation & Exercise",
    text: "Build practical strength, mobility, and confidence through exercises that fit real routines.",
    items: ["Corrective exercise programming", "Mobility and flexibility training", "Strength and stability development", "Posture-focused rehabilitation", "Desk-job pain management", "Sustainable home-exercise planning"],
  },
  {
    number: "03",
    title: "Personal Training & Coaching",
    text: "Move with better technique and progress safely through considered one-to-one guidance.",
    items: ["Safe exercise technique", "Functional strength training", "Movement modification", "Progressive exercise planning", "One-to-one coaching", "Injury-conscious fitness guidance"],
  },
  {
    number: "04",
    title: "Patient Education & Digital Care",
    text: "Clear, evidence-informed physiotherapy support wherever care needs to happen.",
    items: ["Online physiotherapy consultations", "Video-based movement assessment", "Clear exercise demonstrations", "Pain and posture education", "Health-content creation", "Simplifying clinical information"],
  },
];

const experience = [
  {
    period: "2014 — Now",
    role: "Physiotherapist & Personal Trainer",
    title: "Independent Practice",
    meta: "Pakistan · 10+ years",
    text: "Personalized physiotherapy and movement guidance for people dealing with neck, back, posture, mobility, and work-related discomfort.",
    achievements: [
      "Individual assessments built around symptoms, routines, movement patterns, and limitations.",
      "Practical treatment and exercise plans tailored to each client’s life.",
      "Physiotherapy principles combined with personal-training experience.",
      "Clear education that helps clients take an active role in recovery.",
      "Online consultations and personalized exercise guidance beyond the clinic.",
    ],
    stats: ["10+ years", "1:1 care", "Clinic + online"],
  },
  {
    period: "2023 — Now",
    role: "Founder & Physiotherapy Educator",
    title: "Fix My Posture",
    meta: "@fixmyposture.pk",
    text: "Practical educational content that translates clinical knowledge into simple actions for everyday pain and posture.",
    achievements: [
      "Produced accessible videos focused on posture, mobility, and everyday pain.",
      "Reached 7,100+ people and generated 11,300+ views across 13 videos in two weeks.",
      "Built organic discovery among people who were not already following the account.",
      "Created a video shared 63 times through strong audience relevance.",
      "Gained 19 followers from one video by addressing a clear pain problem.",
    ],
    stats: ["11.3K views", "63 shares", "19 followers from one video"],
  },
];

const projects = [
  {
    tag: "Framework",
    title: "The FIX Method",
    text: "A structured approach to understanding pain, identifying contributing factors, selecting targeted exercises, and building a routine that can be sustained in everyday life.",
  },
  {
    tag: "Education",
    title: "Fix My Posture",
    text: "A free physiotherapy education platform that makes posture, movement, and common desk-related pain easier to understand without recycled fitness advice.",
  },
  {
    tag: "Online care",
    title: "Personalized Online Physiotherapy",
    text: "Video-based assessment, movement observation, detailed discussion, and personalized recommendations clients can confidently follow from home.",
  },
  {
    tag: "Content series",
    title: "Desk-Job Pain Education",
    text: "Focused videos that turn clinical knowledge into simple exercises and workstation adjustments viewers can apply during the workday.",
  },
];

const ease = [0.22, 1, 0.36, 1];

function Reveal({ children, className = "", delay = 0 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 42, filter: "blur(10px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, delay, ease }}
    >
      {children}
    </motion.div>
  );
}

function SectionHeading({ kicker, title, description, dark = false }) {
  return (
    <Reveal>
      <div className="section-kicker" data-testid={`${kicker.toLowerCase().replaceAll(" ", "-")}-kicker`}>{kicker}</div>
      <h2 className="section-title">{title}</h2>
      {description && <p className="section-description" style={dark ? { color: "rgba(255,255,255,.66)" } : undefined}>{description}</p>}
    </Reveal>
  );
}

function ActionButton({ children, onClick, variant = "primary", testId }) {
  return (
    <motion.button
      type="button"
      className={`button button-${variant}`}
      onClick={onClick}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.97 }}
      data-testid={testId}
    >
      {children}
    </motion.button>
  );
}

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const lenisRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 65, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 65, damping: 20 });
  const rotateX = useTransform(springY, [-0.5, 0.5], [5, -5]);
  const rotateY = useTransform(springX, [-0.5, 0.5], [-7, 7]);
  const imageY = useTransform(springY, [-0.5, 0.5], [-14, 14]);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.35 });

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true, wheelMultiplier: 0.9 });
    lenisRef.current = lenis;
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);

  const scrollTo = (id) => {
    const target = document.getElementById(id);
    if (!target) return;
    lenisRef.current?.scrollTo(target, { offset: -82, duration: 1.15 });
    setMenuOpen(false);
  };

  const handleHeroMouse = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    mouseX.set((event.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((event.clientY - rect.top) / rect.height - 0.5);
  };

  const headline = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.11, delayChildren: 0.18 } },
  };

  const line = {
    hidden: { y: "112%", rotate: 2 },
    visible: { y: "0%", rotate: 0, transition: { duration: 0.92, ease } },
  };

  return (
    <div className="site-shell">
      <motion.div className="scroll-progress" style={{ scaleX: progress }} />
      <motion.nav className="nav" initial={{ y: -84 }} animate={{ y: 0 }} transition={{ duration: 0.7, ease }} data-testid="site-navigation">
        <div className="nav-inner">
          <button className="brand" onClick={() => scrollTo("top")} data-testid="brand-home">
            <span className="brand-mark">Z.</span>
            <span>Zuhab</span>
          </button>
          <div className="nav-links" data-testid="desktop-nav-links">
            {navItems.map(([label, id]) => (
              <button key={id} className="nav-link" onClick={() => scrollTo(id)} data-testid={`nav-${id}-link`}>{label}</button>
            ))}
            <button className="nav-cta" onClick={() => scrollTo("contact")} data-testid="nav-consultation-button">
              Book a consultation <ArrowUpRight size={15} />
            </button>
          </div>
          <button className="menu-toggle" onClick={() => setMenuOpen((open) => !open)} aria-label="Toggle menu" data-testid="mobile-menu-button">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.28, ease }}
            data-testid="mobile-menu"
          >
            {navItems.map(([label, id]) => (
              <button key={id} className="nav-link" onClick={() => scrollTo(id)} data-testid={`mobile-nav-${id}-link`}>{label}</button>
            ))}
            <button className="nav-cta" onClick={() => scrollTo("contact")} data-testid="mobile-nav-consultation-button">
              Book a consultation <ArrowUpRight size={15} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <main id="top">
        <section className="hero" onMouseMove={handleHeroMouse} data-testid="hero-section">
          <div className="hero-grid">
            <div className="hero-copy">
              <motion.div className="hero-status" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }} data-testid="hero-status">
                <span className="status-dot" /> In-clinic and online physiotherapy
              </motion.div>
              <motion.h1 variants={headline} initial="hidden" animate="visible" aria-label="Real solutions for desk-job pain. In clinic and online.">
                <span className="headline-line"><motion.span variants={line}>Real solutions</motion.span></span>
                <span className="headline-line"><motion.span variants={line}>for <span className="hero-highlight">desk-job pain</span></motion.span></span>
                <span className="headline-line accent"><motion.span variants={line}>In clinic + online.</motion.span></span>
              </motion.h1>
              <motion.p className="hero-lede" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.58, ease }}>
                I’m Zuhab, a Pakistan-based physiotherapist and personal trainer helping people overcome neck, back, and posture-related pain through personalized assessment, practical exercise, and the structured FIX Method.
              </motion.p>
              <motion.div className="hero-actions" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.72, ease }}>
                <ActionButton onClick={() => scrollTo("contact")} testId="hero-book-button">Book a consultation <ArrowUpRight size={17} /></ActionButton>
                <ActionButton onClick={() => scrollTo("expertise")} variant="secondary" testId="hero-services-button">Explore my services <MoveRight size={17} /></ActionButton>
              </motion.div>
              <motion.div className="hero-metrics" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.84, ease }} data-testid="hero-metrics">
                <div className="metric"><strong>10+</strong><span>Years of practice</span></div>
                <div className="metric"><strong>7.1K</strong><span>People reached</span></div>
                <div className="metric"><strong>11.3K</strong><span>Educational views</span></div>
              </motion.div>
            </div>

            <div className="hero-visual-wrap">
              <motion.div
                className="hero-visual"
                style={{ rotateX, rotateY }}
                initial={{ opacity: 0, y: 60, rotate: 3 }}
                animate={{ opacity: 1, y: 0, rotate: 0 }}
                transition={{ duration: 1, delay: 0.34, ease }}
                data-testid="hero-visual"
              >
                <motion.div className="portrait-frame" style={{ y: imageY }}>
                  <img src={heroImage} alt="Physiotherapist guiding a client through rehabilitation" />
                  <div className="image-caption"><span>Assessment-led care</span><span>01 / FIX</span></div>
                </motion.div>
                <motion.div className="method-card" animate={{ y: [0, -10, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>
                  <span>SIGNATURE METHOD</span>
                  <strong>FIX</strong>
                  <p>Assess. Target. Sustain.</p>
                </motion.div>
                <div className="location-chip">Based in Pakistan</div>
                <HeroOrbit />
              </motion.div>
            </div>
          </div>
          <ScrollCue onClick={() => scrollTo("about")} />
          <div className="hero-ghost" aria-hidden="true">POSTURE</div>
        </section>

        <div className="marquee" aria-label="Areas of care" data-testid="care-marquee">
          <div className="marquee-track">
            {[0, 1].map((group) => (
              <div className="marquee-group" key={group} aria-hidden={group === 1}>
                {marqueeItems.map((item) => <span key={`${group}-${item}`}>{item}<ArrowUpRight size={15} /></span>)}
              </div>
            ))}
          </div>
        </div>

        <section id="about" className="chapter-section about" data-testid="about-section">
          <div className="section-inner chapter-layout">
            <div className="chapter-number" aria-hidden="true">01</div>
            <div>
              <SectionHeading
                kicker="About Zuhab"
                title={<>Less generic advice.<br /><em>More personal</em> progress.</>}
              />
              <div className="about-content">
                <Reveal className="about-copy" delay={0.08}>
                  <p className="lead">I’m a physiotherapist and personal trainer with over ten years of hands-on experience helping people move better and manage pain more effectively.</p>
                  <p>I work with individuals whose bodies have been affected by modern routines—long hours at a desk, daily commuting, prolonged standing, limited movement, and repetitive habits.</p>
                  <p>Much of the posture and pain advice online is generic. My approach combines physiotherapy assessment, movement analysis, and practical exercise programming to create solutions that are specific, manageable, and sustainable.</p>
                  <p>Through clinical work, online consultations, and @fixmyposture.pk, I translate professional physiotherapy knowledge into simple actions people can confidently use every day.</p>
                  <div className="signature">Zuhab <span>Physiotherapist & movement coach</span></div>
                </Reveal>
                <Reveal className="about-media" delay={0.16}>
                  <img src={aboutImage} alt="Detailed physiotherapy examination and movement assessment" />
                  <div className="media-note"><strong>Individual first.</strong><span>Assessment before advice, context before exercise.</span></div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        <section id="expertise" className="chapter-section expertise" data-testid="expertise-section">
          <div className="section-inner chapter-layout">
            <div className="chapter-number" aria-hidden="true">02</div>
            <div>
              <SectionHeading
                kicker="How I help"
                title={<>Clinical thinking.<br /><em>Human delivery.</em></>}
                description="I combine clinical knowledge, movement coaching, and patient education to deliver practical support in person and online."
                dark
              />
              <div className="skills-grid">
                {skills.map((skill, index) => (
                  <motion.article
                    className="skill-card"
                    key={skill.number}
                    initial={{ opacity: 0, y: 34 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.7, delay: index * 0.08, ease }}
                    whileHover={{ y: -8 }}
                    data-testid={`skill-card-${skill.number}`}
                  >
                    <div className="skill-top"><span className="skill-number">{skill.number}</span><span className="skill-icon"><ArrowUpRight size={17} /></span></div>
                    <h3>{skill.title}</h3>
                    <p>{skill.text}</p>
                    <ul>{skill.items.map((item) => <li key={item}><Check size={14} />{item}</li>)}</ul>
                  </motion.article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="experience" className="chapter-section experience" data-testid="experience-section">
          <div className="section-inner chapter-layout">
            <div className="chapter-number" aria-hidden="true">03</div>
            <div>
              <div className="experience-head">
                <SectionHeading
                  kicker="Experience"
                  title={<>Built on experience,<br /><em>grounded in reality.</em></>}
                  description="Across a treatment room or a video call, the goal stays the same: turn confusing pain into clear next steps."
                />
                <Reveal className="experience-photo" delay={0.12}>
                  <img src={trainingImage} alt="Strength and movement coaching session" />
                </Reveal>
              </div>
              <div className="timeline">
                {experience.map((item, index) => (
                  <motion.article
                    className="timeline-item"
                    key={item.title}
                    initial={{ opacity: 0, y: 36 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.16 }}
                    transition={{ duration: 0.75, delay: index * 0.08, ease }}
                    data-testid={`experience-item-${index + 1}`}
                  >
                    <div className="timeline-period">{item.period}</div>
                    <div>
                      <p className="timeline-role">{item.role}</p>
                      <h3>{item.title}<span>{item.meta}</span></h3>
                      <p>{item.text}</p>
                      <ul className="achievement-list">{item.achievements.map((achievement) => <li key={achievement}><Check size={14} />{achievement}</li>)}</ul>
                      <div className="stat-strip">{item.stats.map((stat) => <strong key={stat}>{stat}</strong>)}</div>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="work" className="chapter-section projects" data-testid="work-section">
          <div className="section-inner chapter-layout">
            <div className="chapter-number" aria-hidden="true">04</div>
            <div>
              <SectionHeading
                kicker="Selected work"
                title={<>Ideas that help<br /><em>people take action.</em></>}
                description="Each project starts with the same question: what would make professional guidance easier to understand and use?"
              />
              <div className="projects-grid">
                {projects.map((project, index) => (
                  <motion.article
                    className="project-card"
                    key={project.title}
                    initial={{ opacity: 0, y: 36 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.22 }}
                    transition={{ duration: 0.72, delay: index * 0.07, ease }}
                    whileHover={{ y: -8 }}
                    data-testid={`project-card-${index + 1}`}
                  >
                    <div className="project-index">0{index + 1}</div>
                    <div>
                      <span className="project-tag">{project.tag}</span>
                      <h3>{project.title}</h3>
                      <p>{project.text}</p>
                      <button className="project-link" onClick={() => scrollTo("contact")} data-testid={`project-contact-${index + 1}`}>Discuss your goals <ArrowUpRight size={16} /></button>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="chapter-section contact" data-testid="contact-section">
          <div className="section-inner chapter-layout">
            <div className="chapter-number" aria-hidden="true">05</div>
            <Reveal>
              <div className="contact-panel">
                <div className="contact-copy">
                  <div className="section-kicker">Let’s talk</div>
                  <h2 className="section-title">Ready to move better<br />and work <em>without pain?</em></h2>
                  <p>You don’t have to rely on generic exercises or keep managing the same discomfort. Book an in-clinic or online consultation to understand what may be contributing to your pain and create a plan that fits your routine.</p>
                  <ActionButton onClick={() => { window.open("https://wa.me/923038444433?text=Assalam-o-Alaikum%20Zuhab%2C%20I%20would%20like%20to%20book%20a%20physiotherapy%20consultation", "_blank", "noopener,noreferrer"); }} testId="contact-book-button">Book your consultation <ArrowUpRight size={17} /></ActionButton>
                </div>
                <div className="contact-details" data-testid="contact-details">
                  <div className="contact-detail"><span>Clinic</span><a href="https://www.google.com/maps/search/?api=1&query=117%20Block%20B%20Gulberg%202%20Lahore%20Pakistan" target="_blank" rel="noreferrer" data-testid="clinic-directions-link">117, Block B Gulberg 2, Lahore <ArrowUpRight size={15} /></a></div>
                  <div className="contact-detail"><span>WhatsApp</span><a href="https://wa.me/923038444433" target="_blank" rel="noreferrer" data-testid="whatsapp-link">+92 303 8444433 <ArrowUpRight size={15} /></a></div>
                  <div className="contact-detail"><span>Email</span><a href="mailto:Zuhab@fixmyposture.pk" data-testid="email-link">Zuhab@fixmyposture.pk <ArrowUpRight size={15} /></a></div>
                  <div className="contact-detail"><span>Instagram</span><a href="https://instagram.com/fixmyposture.pk" target="_blank" rel="noreferrer" data-testid="instagram-link">@fixmyposture.pk <ArrowUpRight size={15} /></a></div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <footer className="footer" data-testid="site-footer">
        <div className="footer-brand"><span className="brand-mark">Z.</span><div><strong>Zuhab</strong><p>Move better. Work comfortably. Live fully.</p></div></div>
        <div className="footer-social">
          <a href="https://instagram.com/fixmyposture.pk" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram-link"><Instagram size={17} /></a>
          <a href="mailto:Zuhab@fixmyposture.pk" aria-label="Email" data-testid="footer-email-link"><Mail size={17} /></a>
          <button className="brand footer-top-button" onClick={() => scrollTo("top")} aria-label="Back to top" data-testid="footer-back-to-top"><ArrowUp size={17} /></button>
        </div>
        <p className="copyright">© {new Date().getFullYear()} Zuhab Physiotherapy. All rights reserved.</p>
      </footer>
    </div>
  );
}

